import { Chip } from "@mui/material";
import { HStack } from "@zfp-akademie-a-s/zfp-design";
import { HomeIcon, UserIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import {
  BriefcaseIcon,
  ChildIcon,
  CoinsIcon,
  FinancialSuccessIcon,
  RefundIcon,
  SeniorIcon,
  TargetIcon,
} from "Components/_Shared/Icons/Icons";
import { Tile } from "Components/_Shared/Tile";
import { type AnalysisClientItem, type Income, IncomeIncomeType } from "Infrastructure/ApiZfp/Schema";
import { FeatureFlag, useFeatureFlag } from "Infrastructure/Hooks/useFeatureFlag";
import { Spacing } from "Infrastructure/Themes/Themes";
import { useResource } from "Infrastructure/Translations/Resources";
import { formatClientName } from "Infrastructure/Utils/ClientUtils";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { type FC, useState } from "react";
import { EditIncomeDrawer } from "./EditIncomeDrawer";
import { getIncomeTypeResource } from "./IncomeTypeTile";

type Props = {
  income: Income;
  clients: AnalysisClientItem[];
  /** Replaces the tile's own edit drawer. */
  onEdit?: () => void;
};

const getIcon = (incomeType: IncomeIncomeType) => {
  switch (incomeType) {
    case IncomeIncomeType.employment:
      return <BriefcaseIcon />;
    case IncomeIncomeType.self_employment:
      return <FinancialSuccessIcon />;
    case IncomeIncomeType.rental:
      return <HomeIcon />;
    case IncomeIncomeType.parental:
      return <ChildIcon />;
    case IncomeIncomeType.pension:
      return <SeniorIcon />;
    case IncomeIncomeType.disability:
      return <SeniorIcon />;
    case IncomeIncomeType.orphan_or_widow_pension:
      return <RefundIcon />;
    case IncomeIncomeType.other:
      return <TargetIcon />;
  }
};

export const IncomeTile: FC<Props> = ({ income, clients, onEdit }) => {
  const { t } = useResource();

  const isBudgetBulkEntryEnabled = useFeatureFlag(FeatureFlag.BudgetBulkEntry);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);

  const client = clients.find((client) => client.guid === income.clientGuid);
  const totalAmountValue = income.incomeAmounts.reduce((sum, incomeAmount) => sum + incomeAmount.amount.value, 0);
  const currencyCode = income.incomeAmounts[0]?.amount.currencyCode ?? "CZK";
  // several other incomes of one client would otherwise differ only by their amount
  const otherIncomeNames =
    isBudgetBulkEntryEnabled && income.incomeType === IncomeIncomeType.other
      ? income.incomeAmounts
          .map((incomeAmount) => incomeAmount.name)
          .filter(Boolean)
          .join(", ")
      : "";
  const title = [t(getIncomeTypeResource(income.incomeType)), otherIncomeNames].filter(Boolean).join(" · ");

  return (
    <>
      <Tile title={title} icon={getIcon(income.incomeType)} onClick={onEdit ?? (() => setEditDrawerOpen(true))}>
        <HStack alignItems="center" flexWrap="wrap" gap={Spacing.M}>
          {client && (
            <Chip size="small" color="info" icon={<UserIcon />} label={formatClientName(client.firstName, client.surname, null)} />
          )}
          <Chip size="small" icon={<CoinsIcon />} label={formatCurrency(totalAmountValue, currencyCode)} />
        </HStack>
      </Tile>

      {!onEdit && <EditIncomeDrawer open={editDrawerOpen} onClose={() => setEditDrawerOpen(false)} clients={clients} income={income} />}
    </>
  );
};
